import {
  ctorsUnion,
  CtorsUnion
} from "@genesys/client-core/lib/constructors-union";
import { exhaustiveCheck } from "ts-exhaustive-check";
import { ActiveLoadState, LoadDef, LoadType } from "./types";
import { PropertyValueSet } from "@genesys/property";
import { createLoadState } from "./create-load-state";
import { updatedPropertiesPreHook } from "../../../../tools/property-updates-pre-hook";
import { Cmd } from "@typescript-tea/core";
import {
  getDefaultCaseOverrides,
  getIfIntegerChanged,
  PropertyDefinitions
} from "../../../../tools";
import { OperatingCaseGroupEnum } from "@genesys/graphql-types";
import * as OperationTimeGen2 from "../../../../../operation-time-manager";
import * as LoadUpdateHelper from "./load-update-helpers";
import * as SettingsOverride from "../../../../components/settings-overrides";
import * as SharedState from "../../../../../shared-state";
import * as PropertiesSelector from "../../../../../properties-selector";

export type State = ActiveLoadState;

export const init = (
  initialLoadSettings: {
    readonly id: string;
    readonly sortNo: number;
    readonly name: string;
    readonly settings: PropertyValueSet.PropertyValueSet;
    readonly operationTime?: OperationTimeGen2.OperationTime;
    readonly caseOverrides?: SettingsOverride.SettingsOverrides;
  },
  loadDef: LoadDef
): State => {
  const { id, sortNo, name, settings, operationTime, caseOverrides } =
    initialLoadSettings;

  return createLoadState(
    id,
    sortNo,
    loadDef,
    name,
    settings,
    // initialMainMoistureLoadSettings,
    operationTime,
    caseOverrides
  );
};

export const Action = ctorsUnion({
  addTypeToLoad: (loadDef: LoadDef, typeOfLoad: LoadType) => ({
    typeOfLoad,
    loadDef
  }),
  dispatchOperationTime: (action: OperationTimeGen2.Action) => ({
    action
  }),

  dispatchCaseOverrides: (
    action: SettingsOverride.Action,
    loadDef: LoadDef
  ) => ({ action, loadDef }),

  dispatchPropertiesSelector: (
    action: PropertiesSelector.Action,
    loadProperties: ReadonlyArray<string>,
    manualInputKey: string | undefined,
    overridableProperties: ReadonlyArray<string>
  ) => ({
    action,
    loadProperties,
    manualInputKey,
    overridableProperties
  }),
  setCurrentOverrides: (
    overrides: SettingsOverride.SettingsOverrides,
    overridableProperties: ReadonlyArray<string>
  ) => ({
    overrides,
    overridableProperties
  }),
  setLoadName: (name: string) => ({ name }),
  removeTypesFromLoad: (typeOfLoad: LoadType, loadDef: LoadDef) => ({
    typeOfLoad,
    loadDef
  })
});

export type Action = CtorsUnion<typeof Action>;

type ResultsInvalid = boolean;

export function update(
  action: Action,
  state: State,
  sharedState: SharedState.State,
  currentTopLevelCaseOverrides: SettingsOverride.SettingsOverrides,
  currentTopLevelMoistureLoadSettings: PropertyValueSet.PropertyValueSet,
  propertyDefinitions: PropertyDefinitions
): [
  State,
  ResultsInvalid?,
  Cmd<Action>?,
  ReadonlyArray<SharedState.Action | undefined>?
] {
  const setCaseOverrides = (
    overrides: SettingsOverride.SettingsOverrides,
    overridableProperties: ReadonlyArray<string>
  ) => {
    return Object.keys(overrides).reduce((acc, key) => {
      return {
        ...acc,
        [key as OperatingCaseGroupEnum]: PropertyValueSet.keepProperties(
          overridableProperties,
          overrides[key as OperatingCaseGroupEnum]
        )
      };
    }, {} as SettingsOverride.SettingsOverrides);
  };

  const updateOverrideExistence = (
    previousSelections: PropertyValueSet.PropertyValueSet,
    currentSelections: PropertyValueSet.PropertyValueSet,
    manualInputKey: string | undefined,
    overrides: SettingsOverride.SettingsOverrides,
    overridableProperties: ReadonlyArray<string>
  ) => {
    if (manualInputKey === undefined) {
      return overrides;
    }

    const newManualInputKey = getIfIntegerChanged(
      previousSelections,
      currentSelections,
      manualInputKey
    );

    if (newManualInputKey === undefined) {
      return overrides;
    }

    if (newManualInputKey === 0) {
      return getDefaultCaseOverrides(PropertyValueSet.Empty);
    } else {
      return getDefaultCaseOverrides(
        PropertyValueSet.keepProperties(
          overridableProperties,
          currentSelections
        )
      );
    }
  };

  switch (action.type) {
    case "addTypeToLoad": {
      let updatedSettings = { ...state.settings };

      updatedSettings = LoadUpdateHelper.updateSettingsToAddTypesToLoad(
        updatedSettings,
        action.typeOfLoad,
        action.loadDef!
      );

      return [
        {
          ...state,
          settings: updatedSettings
        },
        true
      ];
    }

    case "dispatchOperationTime": {
      const [newOperationTimeState] = OperationTimeGen2.update(
        action.action,
        state.operationTimeState
      );

      return [
        {
          ...state,
          operationTimeState: newOperationTimeState
        },
        true
      ];
    }

    case "dispatchCaseOverrides": {
      const currentState = SettingsOverride.init(
        mergeTopLevelCaseOverrides(state, currentTopLevelCaseOverrides)
      );
      const [newCaseOverrideState] = SettingsOverride.update(
        action.action,
        currentState,
        sharedState,
        action.loadDef.additionalInfo.updateFunction
      );

      return [
        {
          ...state,
          caseOverrides: setCaseOverrides(
            SettingsOverride.getSettings(newCaseOverrideState),
            action.loadDef.properties.overridableProperties
          )
        },
        true
      ];
    }

    case "dispatchPropertiesSelector": {
      const previousSelections = PropertyValueSet.merge(
        state.settings,
        currentTopLevelMoistureLoadSettings
      );

      const oldState = PropertiesSelector.init(previousSelections);

      const [
        newPropertiesSelectorState,
        propertiesSelectorCmd,
        propertiesSelectorSharedAction
      ] = PropertiesSelector.update(
        action.action,
        oldState,
        sharedState,
        "SkipCalculateProperties",
        currentSelections => {
          return updatedPropertiesPreHook(
            previousSelections,
            currentSelections,
            propertyDefinitions
          );
        }
      );

      const currentSelections = PropertiesSelector.getSelectedProperties(
        newPropertiesSelectorState
      );

      const caseOverrides = updateOverrideExistence(
        previousSelections,
        currentSelections,
        action.manualInputKey,
        state.caseOverrides,
        action.overridableProperties
      );
      return [
        {
          ...state,
          settings: PropertyValueSet.keepProperties(
            action.loadProperties,
            currentSelections
          ),
          caseOverrides: caseOverrides
        },
        true,
        Cmd.map(
          subaction =>
            Action.dispatchPropertiesSelector(
              subaction,
              action.loadProperties,
              action.manualInputKey,
              action.overridableProperties
            ),
          propertiesSelectorCmd
        ),
        [propertiesSelectorSharedAction]
      ];
    }

    case "setLoadName": {
      return [
        {
          ...state,
          name: action.name
        },
        true
      ];
    }

    case "setCurrentOverrides": {
      return [
        {
          ...state,
          caseOverrides: setCaseOverrides(
            action.overrides,
            action.overridableProperties
          )
        },
        true
      ];
    }

    case "removeTypesFromLoad": {
      const updatedSettings =
        LoadUpdateHelper.updateSettingsToRemoveTypesFromLoad(
          state.settings,
          action.typeOfLoad,
          action.loadDef!
        );

      // const currentSelectorProperties =
      //   PropertiesSelector.getSelectedProperties(state.propertiesSelectorState);

      return [
        {
          ...state,
          // propertiesSelectorState: PropertiesSelector.init(
          //   PropertyValueSet.merge(updatedSettings, currentSelectorProperties)
          // ),
          settings: updatedSettings
        },
        true
      ];
    }

    default:
      return exhaustiveCheck(action, true);
  }
}

export function mergeTopLevelCaseOverrides(
  state: State,
  topLevelCaseOverrides: SettingsOverride.SettingsOverrides
): SettingsOverride.SettingsOverrides {
  return Object.keys(topLevelCaseOverrides).reduce((acc, key) => {
    return {
      ...acc,
      [key as OperatingCaseGroupEnum]: PropertyValueSet.merge(
        PropertyValueSet.merge(
          state.caseOverrides[key as OperatingCaseGroupEnum],
          state.settings
        ),
        topLevelCaseOverrides[key as OperatingCaseGroupEnum]
      )
    };
  }, {} as SettingsOverride.SettingsOverrides);
}

export function updateLoadName(
  state: State,
  name: string,
  sharedState: SharedState.State,
  currentTopLevelCaseOverrides: SettingsOverride.SettingsOverrides,
  currentTopLevelMoistureLoadSettings: PropertyValueSet.PropertyValueSet,
  propertyDefinitions: PropertyDefinitions
): State {
  const [newState] = update(
    Action.setLoadName(name),
    state,
    sharedState,
    currentTopLevelCaseOverrides,
    currentTopLevelMoistureLoadSettings,
    propertyDefinitions
  );
  return newState;
}

export function overrideLoadSettings(
  state: State,
  prevSettings: PropertyValueSet.PropertyValueSet,
  newSettings: PropertyValueSet.PropertyValueSet,
  loadDef: LoadDef
): State {
  const loadProperties = loadDef.properties.loadProperties;
  const updateFunction = loadDef.additionalInfo.updateFunction;

  let prevSettingsMerged = PropertyValueSet.merge(prevSettings, state.settings);
  let newSettingsMerged = PropertyValueSet.merge(newSettings, state.settings);

  let newDefaults = PropertyValueSet.Empty;

  if (updateFunction) {
    newDefaults = updateFunction(prevSettingsMerged, newSettingsMerged);
  }

  const updatedState = {
    ...state,
    settings: PropertyValueSet.keepProperties(
      loadProperties,
      PropertyValueSet.merge(newDefaults, newSettingsMerged)
    )
  };

  return updatedState;
}
